/**
 * Buy Now Pay Later (BNPL) methods offered at in-store terminal checkout.
 *
 * BNPL renders as a QR code on the Stripe Terminal reader (WisePOS E): the
 * customer scans it and completes the financing application on their own
 * phone. The flow reuses the card pipeline (create-payment-intent →
 * process-payment → poll) with `paymentMethodType` set on both calls.
 *
 * Amount bounds here are UX-only hints (they gate the tile with a tooltip);
 * the backend enforces the authoritative bounds. Keep in sync with
 * bookings-infra `shared/dependencies/python/bookings_utils/bnpl.py`.
 */

export interface BnplMethodConfig {
  /** Stable identifier; extend the union when adding a provider. */
  key: "affirm";
  /** Tile + button label. */
  label: string;
  /** Wire value for the payments API (`paymentMethodType`). */
  paymentMethodType: string;
  /** Minimum transaction amount in dollars. */
  minAmount: number;
  /** Maximum transaction amount in dollars. */
  maxAmount: number;
  /** Accent color for provider branding in checkout UI. */
  color: string;
}

export const BNPL_METHODS: BnplMethodConfig[] = [
  {
    key: "affirm",
    label: "Affirm",
    paymentMethodType: "affirm",
    minAmount: 50,
    maxAmount: 30000,
    color: "#4A4AF4",
  },
];

/**
 * Selection keys are namespaced so a per-salon custom off-platform method
 * that happens to be named "Affirm" can never collide with the BNPL tile
 * (and can never be swallowed by the checkout dispatch's off-platform fallback).
 */
const SELECTION_PREFIX = "Bnpl:";

export const bnplSelectionKey = (method: BnplMethodConfig): string =>
  `${SELECTION_PREFIX}${method.key}`;

export const bnplMethodForSelection = (
  selection: string | null,
): BnplMethodConfig | undefined => {
  if (!selection || !selection.startsWith(SELECTION_PREFIX)) return undefined;
  const key = selection.slice(SELECTION_PREFIX.length);
  return BNPL_METHODS.find((method) => method.key === key);
};

export const isBnplSelection = (selection: string | null): boolean =>
  bnplMethodForSelection(selection) !== undefined;

export const bnplMethodForKey = (
  key: string | null | undefined,
): BnplMethodConfig | undefined =>
  BNPL_METHODS.find((method) => method.key === key);
